/**
 * Reader for the SPEI bank coordinates the `commerce-gate` invoice returns.
 *
 * ── The contract ─────────────────────────────────────────────────────────────────────
 * Confirmed by the backend team (2026-07-29). The coordinates arrive as an ARRAY, since a
 * provider can in principle offer several ways to pay:
 *
 * VERIFIED on the live response (2026-07-29). Note the instructions are nested inside
 * `invoice_data`, and that the CONTAINER key is snake_case while its leaves are camelCase:
 *
 * ```jsonc
 * {
 *   "id": "4fb59de9-…", "status": "pending", "price": "1281", "currency": "MXN",
 *   "payment_data": null,                       // ← always null; NOT where the data lives
 *   "invoice_data": {
 *     "price": "1281", "discount": 0, "setup_price": 680, "features_price": 601,
 *     "payment_instructions": [{                // ← snake_case container…
 *       "account":       "012914002014222862",  // …camelCase leaves. The CLABE.
 *       "beneficiary":   "Plank Online",
 *       "description":   "36005965054666827291",// "concepto" — distinct from `reference`
 *       "dueDate":       "2026-07-30T16:29:03.380Z",
 *       "paymentMethod": "bank-transfer",
 *       "reference":     "1422286",
 *       "targetBank":    "BVA Bancomer"
 *     }],
 *     "test_finalize_payment_url": "https://…"  // TEST ENV ONLY — never rendered, see below
 *   }
 * }
 * ```
 *
 * No amount or currency is returned in the instruction, so the screen falls back to the
 * wizard's own order total. It must NOT fall back to `price`: that is the base total, before
 * any voucher or discount.
 *
 * **`testFinalizePaymentUrl` is deliberately not mapped.** It is a back-door that settles the
 * payment without money moving, present only in test environments. Because
 * {@link SpeiTransferDetails} is the ONLY thing components receive, an unmapped field cannot
 * reach the UI even by accident — and a test pins that.
 *
 * {@link FIELD_ALIASES} still accepts near-miss spellings around the confirmed names, so a
 * casing or snake/camel change on the provider's side degrades to a missing row rather than a
 * broken checkout. No component ever touches a raw response field.
 *
 * Unknown or missing fields come back `null` and their row simply isn't rendered, so a partial
 * payload degrades to "show what we actually got" rather than to blanks or "undefined".
 *
 * Like [`@/utils/session-invoice`](../../../../utils/session-invoice.ts), this is deliberately
 * free of store / network imports so the mapping stays a pure, unit-testable function.
 */
import type { SessionInvoice } from '@/utils/session-invoice';

/** The bank coordinates a buyer needs to make the SPEI transfer. `null` = not supplied. */
export interface SpeiTransferDetails {
  /** CLABE the transfer is sent to (wire: `account`). The one field the flow cannot work without. */
  clabe: string | null;
  /** Reference the buyer quotes so the transfer is matched to this order (wire: `reference`). */
  reference: string | null;
  /** "Concepto" the bank form asks for, separate from the reference (wire: `description`). */
  description: string | null;
  /** Receiving bank name (wire: `targetBank`, e.g. "BVA Bancomer"). */
  bank: string | null;
  /** Account holder the CLABE belongs to. */
  beneficiary: string | null;
  /** Exact amount to transfer, in whole currency units. Absent from the confirmed contract. */
  amount: number | null;
  /** ISO currency code of {@link amount}. Absent from the confirmed contract. */
  currency: string | null;
  /** ISO timestamp after which the coordinates stop being valid (wire: `dueDate`). */
  expiresAt: string | null;
}

/**
 * Wire-name → our field. THE mapping point: the confirmed name leads each list, with tolerated
 * near-misses after it. Correct these lists if the provider's contract changes; nothing else
 * needs to.
 *
 * Matching is case-insensitive and ignores `_`/`-`, so `targetBank`, `target_bank` and
 * `TARGET-BANK` all resolve from one entry.
 */
const FIELD_ALIASES = {
  clabe: ['account', 'clabe', 'clabe_number', 'account_number', 'destination_account'],
  reference: ['reference', 'payment_reference', 'reference_number', 'tracking_key'],
  description: ['description', 'concepto', 'concept', 'payment_concept'],
  bank: ['target_bank', 'bank', 'bank_name', 'institution', 'beneficiary_bank'],
  beneficiary: ['beneficiary', 'beneficiary_name', 'account_holder', 'receiver', 'holder_name'],
  // Deliberately NOT `price`: the invoice row and `invoice_data` both carry one, and it is the
  // BASE total (pre-voucher, pre-discount) — quoting it would tell the buyer to transfer the
  // wrong figure. With no amount in the instruction, the screen falls back to the wizard's own
  // order total, which is the voucher-adjusted amount the buyer was actually shown.
  amount: ['amount', 'amount_due', 'total'],
  currency: ['currency', 'currency_code'],
  expiresAt: ['due_date', 'expires_at', 'expiration_date', 'expiry', 'valid_until'],
} as const satisfies Record<keyof SpeiTransferDetails, readonly string[]>;

/** Comparable form of a key: lower-cased, separators dropped (`Clabe_Number` → `clabenumber`). */
const normaliseKey = (key: string): string => key.toLowerCase().replace(/[_-]/g, '');

/** Plain-object guard that also rejects arrays (a JSON array is an object to `typeof`). */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The array of payment options. Only bank transfers carry SPEI coordinates.
 *
 * Matched through {@link normaliseKey}, not by exact string: the live response nests it as
 * `payment_instructions` while the documented contract calls it `paymentInstructions`, and
 * both must resolve.
 */
const INSTRUCTIONS_KEY = normaliseKey('paymentInstructions');

/**
 * Keys the payload can be wrapped in.
 *
 * `invoice_data` is where it ACTUALLY lives on the live response
 * (`invoice_data.payment_instructions[]`); the rest are tolerated alternatives.
 */
const NESTED_KEYS = [
  'invoice_data',
  'payment_data',
  'provider_data',
  'spei',
  'transfer',
  'payment_details',
  'details',
];

/** The payment-instruction entries on `source`, bank transfers first. */
function instructionsOn(source: Record<string, unknown>): Array<Record<string, unknown>> {
  const key = Object.keys(source).find((k) => normaliseKey(k) === INSTRUCTIONS_KEY);
  const raw = key ? source[key] : undefined;
  const list = (Array.isArray(raw) ? raw : [raw]).filter(isRecord);
  // A provider may offer several methods; the one that carries a CLABE is the transfer. Sort
  // rather than filter, so a payload that omits `paymentMethod` entirely is still read.
  return [...list].sort((a, b) => Number(b.paymentMethod === 'bank-transfer') - Number(a.paymentMethod === 'bank-transfer'));
}

/**
 * Every object the SPEI coordinates could sit on, most-specific FIRST.
 *
 * `paymentInstructions[]` leads because it is the confirmed location and because the invoice
 * row carries unrelated fields of the same name (`description`, `currency`, `price`) that
 * would otherwise shadow the provider's real values. The row itself is searched last as a
 * fallback for a flatter payload.
 */
function candidateSources(invoice: SessionInvoice): Array<Record<string, unknown>> {
  const row = invoice as unknown as Record<string, unknown>;
  const nested = NESTED_KEYS.map((key) => row[key]).filter(isRecord);
  // One more level down: `payment_data: { spei: {…} }` is as plausible as a flat `payment_data`.
  const deeper = nested.flatMap((obj) => NESTED_KEYS.map((key) => obj[key]).filter(isRecord));
  const wrappers = [row, ...nested, ...deeper];
  const instructions = wrappers.flatMap(instructionsOn);
  return [...instructions, ...deeper, ...nested, row];
}

/** First defined, non-empty value for any of `aliases` across `sources`, or `undefined`. */
function pick(sources: Array<Record<string, unknown>>, aliases: readonly string[]): unknown {
  const wanted = aliases.map(normaliseKey);
  for (const source of sources) {
    // Index the source by normalised key ONCE so `clabeNumber` matches the `clabe_number` alias.
    const byKey = new Map(Object.keys(source).map((key) => [normaliseKey(key), source[key]]));
    for (const alias of wanted) {
      const value = byKey.get(alias);
      if (value !== undefined && value !== null && value !== '') return value;
    }
  }
  return undefined;
}

/** A wire value as a trimmed display string, or `null` when absent/blank. */
function asText(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

/**
 * A wire value as a whole-currency number, or `null`. Accepts the numeric-string form the
 * invoice row already uses for `price` (e.g. `"690"`), and tolerates thousands separators.
 */
function asAmount(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const parsed = Number(value.replace(/[\s,]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Map an invoice response onto {@link SpeiTransferDetails}, reading through
 * {@link FIELD_ALIASES}. Pure and total: any shape in, a fully-populated struct of
 * values-or-`null` out.
 */
export function readSpeiDetails(invoice: SessionInvoice): SpeiTransferDetails {
  const sources = candidateSources(invoice);
  return {
    clabe: asText(pick(sources, FIELD_ALIASES.clabe)),
    reference: asText(pick(sources, FIELD_ALIASES.reference)),
    description: asText(pick(sources, FIELD_ALIASES.description)),
    bank: asText(pick(sources, FIELD_ALIASES.bank)),
    beneficiary: asText(pick(sources, FIELD_ALIASES.beneficiary)),
    amount: asAmount(pick(sources, FIELD_ALIASES.amount)),
    currency: asText(pick(sources, FIELD_ALIASES.currency)),
    expiresAt: asText(pick(sources, FIELD_ALIASES.expiresAt)),
  };
}

/**
 * Whether we got enough back to actually instruct a transfer. The CLABE is the account the
 * money goes to, so without it the instructions screen would be decorative; the caller
 * surfaces the retry path instead of showing coordinates nobody can pay to.
 */
export const hasPayableDetails = (details: SpeiTransferDetails): boolean => Boolean(details.clabe);
